import React, { useState, useEffect } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useAuth } from '../../contexts/AuthProvider';

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB');
};

const formatDateTime = (dateTime) => {
  if (!dateTime) return '';
  const d = new Date(dateTime);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const formatEntityType = (entityType) => {
  if (!entityType) return 'Unknown';
  return entityType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, l => l.toUpperCase());
};

const getRoleLabel = (role) => {
  if (!role) return '';
  if (typeof role === 'string') return role;
  return role.label || role.name || role.value || (role.id ? String(role.id) : String(role));
};

const getRoleValue = (role) => {
  if (!role) return '';
  if (typeof role === 'string') return role;
  return role.value || role.name || role.label || (role.id ? String(role.id) : String(role));
};

// Dummy documents data
const documentsData = [
  { id: 1, category: 'CV', name: 'CV - John Doe', fileName: 'cv_johndoe.pdf', size: '2.0 MB', uploadDate: '02/11/2030', uploadBy: 'johndoe_101', status: 'active' },
  { id: 2, category: 'Certificate', name: 'ISO 9001 Auditor Certificate', fileName: 'iso_9001_auditor.pdf', size: '2.0 MB', uploadDate: '02/11/2030', uploadBy: 'johndoe_101', status: 'expired' },
  { id: 3, category: 'Training', name: 'Environmental Management Training', fileName: 'env_management.pdf', size: '2.0 MB', uploadDate: '02/11/2030', uploadBy: 'johndoe_101', status: 'active' }
];

// Dummy performance data
const performanceData = [
  { id: 1, project: 'ISO 9001', client: 'PT Digital Inovasi', services: 'Certification - Initial', mandays: 3, status: 'success' },
  { id: 2, project: 'ISO 27001', client: 'PT Global Technology', services: 'Surveillance 1st', mandays: 1, status: 'success' }
];

const normalizeCompetencyStatus = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('pending')) return 'pending';
  if (value.includes('approved')) return 'approved';
  if (value.includes('rejected')) return 'rejected';
  if (value.includes('revision')) return 'need_revision';
  return value;
};

const ListUsers = () => {
  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canListUsers = (key) => can(key, 'listUsers');

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [searchQuery, setSearchQuery] = useState('');
  const [userListPage, setUserListPage] = useState(0);
  const [userListSize] = useState(10);
  const [userListTotalPages, setUserListTotalPages] = useState(0);
  const [userListTotalElements, setUserListTotalElements] = useState(0);
  const [accountForm, setAccountForm] = useState({
    username: '',
    email: '',
    empId: '',
    emergencyContact: '',
    emergencyAddress: ''
  });
  const [passwordResetMessage, setPasswordResetMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [createForm, setCreateForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    employeeId: '',
    departmentId: '',
    jobTitleId: '',
    branchIds: [],
    roles: [],
    nohp: '',
    address: '',
    emergencyContact: '',
    emergencyAddress: '',
    businessLocation: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    status: '1'
  });
  const [editForm, setEditForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    employeeId: '',
    departmentId: '',
    jobTitleId: '',
    branchIds: [],
    roles: [],
    nohp: '',
    address: '',
    emergencyContact: '',
    emergencyAddress: '',
    businessLocation: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    status: '1'
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [createProfilePicture, setCreateProfilePicture] = useState(null);
  const [createSignature, setCreateSignature] = useState(null);
  const [editProfilePicture, setEditProfilePicture] = useState(null);
  const [editSignature, setEditSignature] = useState(null);
  const [editProfilePreview, setEditProfilePreview] = useState(null);
  const [editSignaturePreview, setEditSignaturePreview] = useState(null);

  const getPhotoUrl = (photoFileName) => {
    if (!photoFileName) return null;
    const photoValue = String(photoFileName);
    if (
      photoValue.startsWith('http://') ||
      photoValue.startsWith('https://') ||
      photoValue.startsWith('data:image/')
    ) {
      return photoValue;
    }
    const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
    return `${apiUrl}/api/public/displayimage/${encodeURIComponent(photoValue)}`;
  };

  const getUserPhotoUrl = (user) => {
    if (!user) return null;
    return getPhotoUrl(
      user.photo ||
      user.photoUrl ||
      user.avatarUrl ||
      user.profileImageUrl ||
      user.imageUrl ||
      user.profilePicture ||
      user.avatar ||
      user.image
    );
  };

  const getSignatureUrl = (signatureFileName) => {
    if (!signatureFileName) return null;
    const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
    return `${apiUrl}/api/public/displayimage/${encodeURIComponent(signatureFileName)}`;
  };

  const getCompetencyEmployeeName = (row) => {
    const rowEmployeeId = String(row?.employeeId || '').trim();
    const selectedEmployeeId = String(selectedUser?.employeeId || '').trim();
    if (selectedUser && selectedEmployeeId && rowEmployeeId && selectedEmployeeId === rowEmployeeId) {
      return getUserName(selectedUser);
    }
    if (selectedUser && !rowEmployeeId) return getUserName(selectedUser);
    return rowEmployeeId || getUserName(selectedUser) || '';
  };
  const [isCreating, setIsCreating] = useState(false);
  const [competencyRows, setCompetencyRows] = useState([]);
  const [competencyLoading, setCompetencyLoading] = useState(false);
  const [competencySearch, setCompetencySearch] = useState('');
  const [competencyStandardFilter, setCompetencyStandardFilter] = useState('');
  const [competencyStatusFilter, setCompetencyStatusFilter] = useState('');
  
  // Activity Log state
  const [activityLogRows, setActivityLogRows] = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [activityLogSearch, setActivityLogSearch] = useState('');
  const [activityLogPage, setActivityLogPage] = useState(0);
  const [activityLogSize, setActivityLogSize] = useState(10);
  const [activityLogTotalPages, setActivityLogTotalPages] = useState(0);
  const [activityLogTotalElements, setActivityLogTotalElements] = useState(0);
  const [showActivityDetails, setShowActivityDetails] = useState(false);
  const [selectedActivityEntry, setSelectedActivityEntry] = useState(null);

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/md/departement');
      setDepartments(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const res = await api.get('/md/job-title');
      setJobTitles(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch job titles:', err);
    }
  };

  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const res = await api.get('/auth/role-options');
      setRoleOptions(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches');
      const data = res?.data?.data ?? res?.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const fetchCompetencyRequests = async (userId) => {
    if (!userId) {
      setCompetencyRows([]);
      return;
    }
    setCompetencyLoading(true);
    try {
      const res = await api.get(`/audit/competencies/requests/user/${userId}`);
      setCompetencyRows(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch competency requests:', err);
      setCompetencyRows([]);
    } finally {
      setCompetencyLoading(false);
    }
  };

  const fetchActivityLog = async (userId, page = 0, size = 10) => {
    if (!userId) {
      console.log('📭 No userId provided, skipping fetch');
      setActivityLogRows([]);
      setActivityLogTotalPages(0);
      setActivityLogTotalElements(0);
      return;
    }
    
    setActivityLogLoading(true);
    try {
      console.log(`🔍 Fetching activity log for user: ${userId}, page: ${page}, size: ${size}`);
      
      const response = await api.get(`/audit-history/user?userId=${userId}&page=${page}&size=${size}`);
      const data = response.data;
      
      console.log('✅ Raw API response:', data);
      
      if (data && data.content) {
        // Process the data to ensure consistent format
        const processedContent = data.content.map(item => ({
          id: item.id,
          entityType: item.entityType,
          entityId: item.entityId,
          actionType: item.actionType,
          description: item.description,
          actionTimestamp: item.actionTimestamp,
          createdBy: item.userName || 'Unknown',
          userId: item.userId,
          ipAddress: item.ipAddress,
          statusChange: item.statusChangeFrom && item.statusChangeTo ? {
            oldStatus: item.statusChangeFrom,
            newStatus: item.statusChangeTo
          } : null,
          reviewNotes: item.approvalNotes,
          additionalData: item.oldValues || item.newValues ? {
            oldValues: item.oldValues,
            newValues: item.newValues,
            changedFields: item.changedFields
          } : null
        }));

        setActivityLogRows(processedContent);
        setActivityLogTotalPages(data.totalPages || 0);
        setActivityLogTotalElements(data.totalElements || 0);
        setActivityLogPage(page);
        
        console.log(`📊 Loaded ${processedContent.length} activity log entries`);
      } else {
        console.log('📭 No activity data in response');
        setActivityLogRows([]);
        setActivityLogTotalPages(0);
        setActivityLogTotalElements(0);
      }
    } catch (error) {
      console.error('❌ Error fetching activity log:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Set empty state on error
      setActivityLogRows([]);
      setActivityLogTotalPages(0);
      setActivityLogTotalElements(0);
      
      // Show user-friendly error message
      if (error.response?.status === 500) {
        console.error('🔥 Server error - audit tables may not exist or there may be data issues');
      }
    } finally {
      setActivityLogLoading(false);
    }
  };

  const fetchUsers = async (search = '', forceSelectFirst = false, page = 0) => {
    setLoadingUsers(true);
    try {
      let res;
      if (search && search.trim()) {
        res = await api.get('/md/internal-users/search', {
          params: { searchTerm: search.trim(), page, size: userListSize }
        });
      } else {
        res = await api.get('/md/internal-users', { params: { page, size: userListSize } });
      }
      const data = res?.data || {};
      const content = data.content || [];
      setUsers(content);
      setUserListPage(page);
      setUserListTotalPages(data.totalPages || 0);
      setUserListTotalElements(data.totalElements || data.total || 0);
      if (content.length === 0) {
        setSelectedUser(null);
      } else if (forceSelectFirst || !selectedUser) {
        setSelectedUser(content[0]);
      } else {
        const refreshed = content.find(u => String(u.id) === String(selectedUser.id));
        setSelectedUser(refreshed || content[0]);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchJobTitles();
    fetchRoles();
    fetchBranches();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchUsers(searchQuery, false, 0);
  }, [searchQuery]);

  useEffect(() => {
    if (activeTab === 'competency') {
      fetchCompetencyRequests(selectedUser?.id);
    } else if (activeTab === 'activity') {
      fetchActivityLog(selectedUser?.id, activityLogPage, activityLogSize);
    }
  }, [activeTab, selectedUser]);

  // Reset activity log pagination when user changes
  useEffect(() => {
    if (selectedUser) {
      setActivityLogPage(0);
      if (activeTab === 'activity') {
        fetchActivityLog(selectedUser.id, 0, activityLogSize);
      }
    }
  }, [selectedUser]);

  const getUserName = (user) => {
    if (!user) return '';
    const first = user.firstName || '';
    const last = user.lastName || '';
    const full = `${first} ${last}`.trim();
    return full || user.username || '';
  };

  const getUserInitials = (user) => {
    if (!user) return '';
    const first = user.firstName || '';
    const last = user.lastName || '';
    if (first || last) {
      return `${first.charAt(0) || ''}${last.charAt(0) || ''}`.toUpperCase();
    }
    const username = user.username || '';
    return username.slice(0, 2).toUpperCase();
  };

  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'N/A';
    const dept = departments.find(d => String(d.id) === String(departmentId));
    return dept ? dept.name : 'N/A';
  };

  const getJobTitleName = (jobTitleId) => {
    if (!jobTitleId) return 'N/A';
    const job = jobTitles.find(j => String(j.id) === String(jobTitleId));
    return job ? job.name : 'N/A';
  };

  const getUserStatus = (user) => {
    const status = user?.status;
    if (status === '1' || status === 1 || status === 'active') return 'active';
    if (status === '0' || status === 0 || status === 'inactive') return 'inactive';
    return status || 'inactive';
  };

  const getInitialsColor = (initials) => {
    const colors = {
      'RJ': '#6366f1', 'JD': '#f97316', 'CC': '#06b6d4', 'EE': '#8b5cf6', 'MT': '#ec4899',
      'AP': '#14b8a6', 'BN': '#f43f5e', 'DS': '#6366f1', 'FT': '#f97316', 'GL': '#06b6d4',
      'HS': '#8b5cf6', 'IR': '#ec4899', 'JW': '#14b8a6', 'KL': '#f43f5e', 'ZM': '#6366f1'
    };
    return colors[initials] || '#6366f1';
  };

  const renderUserAvatar = (user, size = 40) => {
    const photoUrl = getUserPhotoUrl(user);
    const initials = getUserInitials(user);
    const isLarge = size >= 56;

    return (
      <span
        className="avatar rounded-circle flex-shrink-0"
        style={{
          width: size,
          height: size,
          minWidth: size,
          backgroundColor: photoUrl ? '#f8fafc' : getInitialsColor(initials),
          backgroundImage: photoUrl ? `url(${photoUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: isLarge ? 20 : 14,
          border: photoUrl ? '2px solid #e5e7eb' : 'none',
          boxShadow: isLarge ? '0 10px 28px rgba(15, 23, 42, 0.08)' : 'none'
        }}
        title={getUserName(user)}
      >
        {!photoUrl ? initials : ''}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="badge bg-success">Active</span>;
    } else if (status === 'expired') {
      return <span className="badge bg-danger">Expired</span>;
    }
    return <span className="badge bg-secondary">{status}</span>;
  };

  const competencyStatusBadge = (status) => {
    switch (status) {
      case 'approved':
      case 'Approved':
        return <span className="badge bg-success">Approved</span>;
      case 'rejected':
      case 'Rejected':
        return <span className="badge bg-danger">Rejected</span>;
      case 'pending':
      case 'Pending Approval':
        return <span className="badge bg-warning text-dark">Pending Approval</span>;
      case 'revision_requested':
      case 'Revision Requested':
      case 'need_revision':
      case 'Need Revision':
        return <span className="badge bg-info text-dark">Need Revision</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  const getActionBadge = (actionType) => {
    const map = {
      CREATE: 'bg-success',
      UPDATE: 'bg-info',
      DELETE: 'bg-danger',
      APPROVE: 'bg-success',
      REJECT: 'bg-danger',
      REQUEST_REVISION: 'bg-warning'
    };
    return <span className={`badge text-white ${map[actionType] || 'bg-light'}`}>{actionType}</span>;
  };

  const getAuditStatusBadge = (status) => {
    const map = {
      pending: 'bg-warning',
      approved: 'bg-success',
      rejected: 'bg-danger',
      revision_requested: 'bg-info',
      need_revision: 'bg-info'
    };
    return <span className={`badge text-white ${map[status] || 'bg-light'}`}>{status}</span>;
  };

  const handleViewActivityDetails = (entry) => {
    setSelectedActivityEntry(entry);
    setShowActivityDetails(true);
  };

  useEffect(() => {
    if (selectedUser) {
      setAccountForm({
        username: selectedUser.username || getUserName(selectedUser).toLowerCase().replace(/\s+/g, '_'),
        email: selectedUser.email || '',
        empId: selectedUser.employeeId || '',
        emergencyContact: selectedUser.emergencyContact || '',
        emergencyAddress: selectedUser.emergencyAddress || ''
      });
      setPasswordResetMessage('');
    }
  }, [selectedUser]);

  useEffect(() => {
    setCompetencySearch('');
    setCompetencyStandardFilter('');
    setCompetencyStatusFilter('');
  }, [selectedUser]);

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveAccount = async () => {
    if (!selectedUser) return;
    try {
      const formData = new FormData();
      formData.append('username', accountForm.username || selectedUser.username || '');
      formData.append('firstName', selectedUser.firstName || '');
      formData.append('lastName', selectedUser.lastName || '');
      formData.append('email', accountForm.email || selectedUser.email || '');
      formData.append('password', ''); // keep empty so password unchanged
      formData.append('employeeId', accountForm.empId || selectedUser.employeeId || '');
      const deptId = selectedUser.departementId || selectedUser.departmentId || '0';
      const jobTitleId = selectedUser.jobTitleId || '0';
      formData.append('departmentId', String(deptId || '0'));
      formData.append('jobTitleId', String(jobTitleId || '0'));

      const roles = Array.isArray(selectedUser.roles) ? selectedUser.roles : [];
      roles.forEach(role => formData.append('roles', getRoleValue(role)));

      formData.append('nohp', selectedUser.nohp || '');
      formData.append('address', selectedUser.address || '');
      formData.append('emergencyContact', accountForm.emergencyContact || '');
      formData.append('emergencyAddress', accountForm.emergencyAddress || '');
      formData.append('businessLocation', selectedUser.businessLocation || '');
      formData.append('country', selectedUser.country || '');
      formData.append('state', selectedUser.state || '');
      formData.append('city', selectedUser.city || '');
      formData.append('postalCode', selectedUser.postalCode || '');
      formData.append('status', selectedUser.status || '1');

      await api.put(`/md/internal-users/${selectedUser.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPasswordResetMessage('Account updated successfully.');
      setTimeout(() => setPasswordResetMessage(''), 3000);
      await fetchUsers(searchQuery);
    } catch (err) {
      console.error('Failed to update account:', err);
      setPasswordResetMessage('Failed to update account.');
      setTimeout(() => setPasswordResetMessage(''), 3000);
    }
  };

  const handleDeleteUser = async () => {
    if (!canListUsers('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (!selectedUser) return;
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/md/internal-users/${selectedUser.id}`);
      setSelectedUser(null);
      setSuccessMessage('User deleted successfully.');
      setShowSuccessModal(true);
      await fetchUsers(searchQuery, true);
    } catch (err) {
      console.error('Failed to delete user:', err);
      setFailMessage('Failed to delete user: ' + (err.response?.data?.error || err.message));
      setShowFailModal(true);
    }
  };

  const handleResetPassword = () => {
    setPasswordResetMessage(`Password reset link sent to ${accountForm.email}`);
    setTimeout(() => setPasswordResetMessage(''), 5000);
  };

  const openEditModal = async () => {
    if (!selectedUser) return;
    const departmentIdValue = selectedUser.departementId || selectedUser.departmentId || '';
    const roles = Array.isArray(selectedUser.roles)
      ? selectedUser.roles
          .map(r => getRoleValue(r) || r?.name || r)
          .filter(Boolean)
      : [];

    let branchIds = [];
    try {
      const branchRes = await api.get(`/user-branches/user/${selectedUser.id}`);
      const list = branchRes.data?.data ?? branchRes.data;
      if (Array.isArray(list) && list.length > 0) {
        branchIds = list.map(b => String(b?.id ?? b?.branchId ?? '')).filter(Boolean);
      }
    } catch (_) {}

    setEditForm({
      username: selectedUser.username || getUserName(selectedUser).toLowerCase().replace(/\s+/g, '_'),
      firstName: selectedUser.firstName || '',
      lastName: selectedUser.lastName || '',
      email: selectedUser.email || '',
      employeeId: selectedUser.employeeId || '',
      departmentId: String(departmentIdValue || ''),
      jobTitleId: String(selectedUser.jobTitleId || ''),
      branchIds,
      roles,
      nohp: selectedUser.nohp || '',
      address: selectedUser.address || '',
      emergencyContact: selectedUser.emergencyContact || '',
      emergencyAddress: selectedUser.emergencyAddress || '',
      businessLocation: selectedUser.businessLocation || '',
      country: selectedUser.country || '',
      state: selectedUser.state || '',
      city: selectedUser.city || '',
      postalCode: selectedUser.postalCode || '',
      status: selectedUser.status || '1'
    });

    setEditProfilePicture(null);
    setEditSignature(null);
    setEditProfilePreview(null);
    setEditSignaturePreview(null);

    // Set preview for existing images if available
    if (selectedUser.photo) {
      setEditProfilePreview(getPhotoUrl(selectedUser.photo));
    }
    if (selectedUser.signature) {
      setEditSignaturePreview(getSignatureUrl(selectedUser.signature));
    }

    const modal = new window.bootstrap.Modal(document.getElementById('modal-edit-user'));
    modal.show();
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSelectChange = (name, value) => {
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditProfilePictureChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setEditProfilePicture(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditProfilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSignatureChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setEditSignature(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearEditProfilePictureSelection = () => {
    setEditProfilePicture(null);
    // revert preview to existing photo if any
    setEditProfilePreview(selectedUser?.photo ? getPhotoUrl(selectedUser.photo) : null);
  };

  const clearEditSignatureSelection = () => {
    setEditSignature(null);
    // revert preview to existing signature if any
    setEditSignaturePreview(selectedUser?.signature ? getSignatureUrl(selectedUser.signature) : null);
  };

  const handleUpdateUser = async (e) => {
    if (!canListUsers('edit')) { alert('You do not have permission to edit.'); return; }
    e && e.preventDefault();
    if (!selectedUser) return;
    if (
      !editForm.username ||
      !editForm.firstName ||
      !editForm.lastName ||
      !editForm.email ||
      !editForm.employeeId ||
      !editForm.departmentId ||
      !editForm.jobTitleId ||
      !editForm.nohp ||
      !Array.isArray(editForm.roles) || editForm.roles.length === 0 ||
      !editForm.address ||
      !editForm.country ||
      !editForm.state ||
      !editForm.city ||
      !editForm.postalCode
    ) {
      setFailMessage('Please fill all required fields.');
      setShowFailModal(true);
      return;
    }

    setIsUpdating(true);
    try {
      const formData = new FormData();
      formData.append('username', editForm.username || '');
      formData.append('firstName', editForm.firstName || '');
      formData.append('lastName', editForm.lastName || '');
      formData.append('email', editForm.email || '');
      formData.append('password', '');

      formData.append('employeeId', editForm.employeeId || '0');
      formData.append('departmentId', editForm.departmentId || '0');
      formData.append('jobTitleId', editForm.jobTitleId || '0');
      (editForm.roles || []).forEach(role => formData.append('roles', role));

      formData.append('nohp', editForm.nohp || '');
      formData.append('address', editForm.address || '');
      formData.append('emergencyContact', editForm.emergencyContact || '');
      formData.append('emergencyAddress', editForm.emergencyAddress || '');
      formData.append('businessLocation', editForm.businessLocation || '');
      formData.append('country', editForm.country || '');
      formData.append('state', editForm.state || '');
      formData.append('city', editForm.city || '');
      formData.append('postalCode', editForm.postalCode || '');
      formData.append('status', editForm.status || '1');

      if (editProfilePicture) {
        formData.append('profilePicture', editProfilePicture);
      }
      if (editSignature) {
        formData.append('signature', editSignature);
      }

      await api.put(`/md/internal-users/${selectedUser.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (editForm.branchIds && editForm.branchIds.length > 0) {
        try {
          await api.put(`/user-branches/user/${selectedUser.id}/branches`, {
            branchIds: editForm.branchIds.map(Number)
          });
        } catch (err) {
          console.warn('Could not update user branches:', err);
        }
      } else {
        try {
          await api.delete(`/user-branches/user/${selectedUser.id}/branches`);
        } catch (err) {
          console.warn('Could not clear user branches:', err);
        }
      }

      const modalEl = document.getElementById('modal-edit-user');
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();

      setSuccessMessage('User updated successfully.');
      setShowSuccessModal(true);
      await fetchUsers(searchQuery);
    } catch (err) {
      console.error('Failed to update user:', err);
      setFailMessage('Failed to update user: ' + (err.response?.data?.error || err.message));
      setShowFailModal(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      employeeId: '',
      departmentId: '',
      jobTitleId: '',
      branchIds: [],
      roles: [],
      nohp: '',
      address: '',
      emergencyContact: '',
      emergencyAddress: '',
      businessLocation: '',
      country: '',
      state: '',
      city: '',
      postalCode: '',
      status: '1'
    });
    setCreateProfilePicture(null);
    setCreateSignature(null);
    const modal = new window.bootstrap.Modal(document.getElementById('modal-create-user'));
    modal.show();
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSelectChange = (name, value) => {
    setCreateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateProfilePictureChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCreateProfilePicture(file);
  };

  const handleCreateSignatureChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCreateSignature(file);
  };

  const handleCreateUser = async (e) => {
    if (!canListUsers('create')) { alert('You do not have permission to create.'); return; }
    e && e.preventDefault();
    if (
      !createForm.firstName ||
      !createForm.lastName ||
      !createForm.username ||
      !createForm.email ||
      !createForm.password ||
      !createForm.employeeId ||
      !createForm.departmentId ||
      !createForm.jobTitleId ||
      !createForm.nohp ||
      !Array.isArray(createForm.roles) || createForm.roles.length === 0 ||
      !createForm.address ||
      !createForm.country ||
      !createForm.state ||
      !createForm.city ||
      !createForm.postalCode ||
      (createForm.status !== '1' && createForm.status !== '0' && createForm.status !== 1 && createForm.status !== 0)
    ) {
      setFailMessage('Please fill all required fields.');
      setShowFailModal(true);
      return;
    }
    setIsCreating(true);
    try {
      const formData = new FormData();
      formData.append('username', createForm.username);
      formData.append('firstName', createForm.firstName);
      formData.append('lastName', createForm.lastName);
      formData.append('email', createForm.email);
      formData.append('password', createForm.password);
      formData.append('employeeId', createForm.employeeId);
      formData.append('departmentId', createForm.departmentId || '0');
      formData.append('jobTitleId', createForm.jobTitleId || '0');
      (createForm.roles || []).forEach(role => formData.append('roles', role));
      formData.append('nohp', createForm.nohp || '');
      formData.append('address', createForm.address || '');
      formData.append('emergencyContact', createForm.emergencyContact || '');
      formData.append('emergencyAddress', createForm.emergencyAddress || '');
      formData.append('businessLocation', createForm.businessLocation || '');
      formData.append('country', createForm.country || '');
      formData.append('state', createForm.state || '');
      formData.append('city', createForm.city || '');
      formData.append('postalCode', createForm.postalCode || '');
      formData.append('status', createForm.status || '1');

      if (createProfilePicture) {
        formData.append('profilePicture', createProfilePicture);
      }
      if (createSignature) {
        formData.append('signature', createSignature);
      }

      const response = await api.post('/md/internal-users', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const savedUserId = response?.data?.internalUser?.id || response?.data?.id;
      if (savedUserId && createForm.branchIds && createForm.branchIds.length > 0) {
        try {
          await api.post(`/user-branches/user/${savedUserId}/branches`, {
            branchIds: createForm.branchIds.map(Number)
          });
        } catch (err) {
          console.warn('Could not assign branches to user:', err);
        }
      }

      const modalEl = document.getElementById('modal-create-user');
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();
      setSuccessMessage('User created successfully.');
      setShowSuccessModal(true);
      await fetchUsers(searchQuery, true);
    } catch (err) {
      console.error('Failed to create user:', err);
      setFailMessage('Failed to create user: ' + (err.response?.data?.error || err.message));
      setShowFailModal(true);
    } finally {
      setIsCreating(false);
    }
  };

  const competencyRowsForUser = competencyRows;
  const competencyStandardOptions = Array.from(new Set(
    competencyRowsForUser.map(row => row.standard).filter(Boolean)
  ));

  const filteredCompetencyRows = competencyRowsForUser.filter((row) => {
    const query = competencySearch.trim().toLowerCase();
    if (query) {
      const haystack = `${getCompetencyEmployeeName(row) || ''} ${row.standard || ''} ${row.sectors || ''}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    if (competencyStandardFilter && String(row.standard) !== String(competencyStandardFilter)) {
      return false;
    }
    if (competencyStatusFilter && normalizeCompetencyStatus(row.status) !== competencyStatusFilter) {
      return false;
    }
    return true;
  });

  return (
    <>
    <div className="">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">User Details</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="row">
            {/* Left Sidebar - User List */}
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search ..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="btn btn-primary" onClick={openCreateModal}>Create</button>
                  </div>
                </div>
                <div className="list-group list-group-flush">
                  {loadingUsers ? (
                    <div className="text-center py-4 text-muted">Loading users...</div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-4 text-muted">No users found</div>
                  ) : users.map(user => (
                    <button
                      key={user.id}
                      className={`list-group-item list-group-item-action text-start py-3 ${selectedUser && selectedUser.id === user.id ? 'active' : ''}`}
                      onClick={() => setSelectedUser(user)}
                      style={{ border: 'none', borderBottom: '1px solid #e5e7eb' }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        {renderUserAvatar(user, 40)}
                        <div className="flex-grow-1">
                          <div className="fw-500">{getUserName(user)}</div>
                          <div className="small text-muted">{user.employeeId || ''}</div>
                        </div>
                        {getUserStatus(user) === 'active' && (
                          <div style={{width: 8, height: 8, borderRadius: '50%', background: '#22c55e'}}></div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {userListTotalPages > 1 && (
                  <div className="card-footer d-flex align-items-center justify-content-center py-2">
                    <ul className="pagination pagination-sm m-0">
                      <li className={`page-item ${userListPage === 0 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchUsers(searchQuery, false, userListPage - 1)} disabled={userListPage === 0}>←</button>
                      </li>
                      {Array.from({ length: Math.min(userListTotalPages, 5) }, (_, i) => {
                        const pageNum = Math.max(0, userListPage - 2) + i;
                        if (pageNum >= userListTotalPages) return null;
                        return (
                          <li key={pageNum} className={`page-item ${pageNum === userListPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchUsers(searchQuery, false, pageNum)}>{pageNum + 1}</button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${userListPage >= userListTotalPages - 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchUsers(searchQuery, false, userListPage + 1)} disabled={userListPage >= userListTotalPages - 1}>→</button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - User Details */}
            <div className="col-md-8">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  {selectedUser ? (
                  <>
                  <div className="d-flex align-items-center gap-3">
                    {renderUserAvatar(selectedUser, 72)}
                    <div>
                      <h5 className="mb-1">{getUserName(selectedUser)}</h5>
                      <div className="small text-muted">{selectedUser.employeeId || ''}</div>
                    </div>
                  </div>
                  <span
                    className={`badge ${getUserStatus(selectedUser) === 'active' ? 'bg-success' : 'bg-danger'}`}
                    style={{height: 'fit-content'}}
                  >
                    {getUserStatus(selectedUser) === 'active' ? '● Active' : '● Inactive'}
                  </span>
                  </>
                  ) : (
                    <div className="text-muted">No user selected</div>
                  )}
                </div>

                {/* Tabs */}
                <div className="card-header border-bottom-0">
                  <ul className="nav nav-tabs card-header-tabs" role="tablist">
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-profile"
                        className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                        role="tab"
                        onClick={() => setActiveTab('profile')}
                      >
                        Info Profile
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-document"
                        className={`nav-link ${activeTab === 'document' ? 'active' : ''}`}
                        role="tab"
                        onClick={() => setActiveTab('document')}
                      >
                        Document
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-activity"
                        className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`}
                        role="tab"
                        onClick={() => setActiveTab('activity')}
                      >
                        Activity Log
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-competency"
                        className={`nav-link ${activeTab === 'competency' ? 'active' : ''}`}
                        role="tab"
                        onClick={() => setActiveTab('competency')}
                      >
                        Competency
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-performance"
                        className={`nav-link ${activeTab === 'performance' ? 'active' : ''}`}
                        role="tab"
                        onClick={() => setActiveTab('performance')}
                      >
                        Performance
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-security"
                        className={`nav-link ${activeTab === 'security' ? 'active' : ''}`}
                        role="tab"
                        onClick={() => setActiveTab('security')}
                      >
                        Security
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Tab Content */}
                <div className="card-body">
                    <div className="tab-content">
                    {/* Info Profile Tab */}
                    {activeTab === 'profile' && selectedUser && (
                        <div>
                        <div className="d-flex justify-content-end mb-3">
                          <button type="button" className="btn btn-outline-primary btn-sm" onClick={openEditModal}>
                            Edit
                          </button>
                        </div>
                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Username</div>
                                <div className="fw-500">{selectedUser.username || getUserName(selectedUser).toLowerCase().replace(/\s+/g, '_')}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Employee ID</div>
                                <div className="fw-500">{selectedUser.employeeId || ''}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Email</div>
                                <div className="fw-500">{selectedUser.email || ''}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Department</div>
                                <div className="fw-500">{getDepartmentName(selectedUser.departementId || selectedUser.departmentId)}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Phone</div>
                                <div className="fw-500">{selectedUser.nohp || ''}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Job Title</div>
                                <div className="fw-500">{getJobTitleName(selectedUser.jobTitleId)}</div>
                            </div>
                            </div>
                            <div className="col-12">
                            <div>
                                <div className="text-muted small mb-1">Address</div>
                                <div className="fw-500">{selectedUser.address || ''}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                              <div className="text-muted small mb-1">Emergency Contact</div>
                              <div className="fw-500">{selectedUser.emergencyContact || ''}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                              <div className="text-muted small mb-1">Emergency Address</div>
                              <div className="fw-500">{selectedUser.emergencyAddress || ''}</div>
                            </div>
                            </div>
                            <div className="col-md-6">
                            <div>
                                <div className="text-muted small mb-1">Join Date</div>
                                <div className="fw-500">{formatDate(selectedUser.cdate)}</div>
                            </div>
                            </div>
                        </div>

                        {/* User Roles */}
                        <hr />
                        <div className="mt-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="mb-0">User Roles</h6>
                            {/* <button className="btn btn-sm btn-outline-primary">Edit Roles</button> */}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                            {(selectedUser.roles || []).map((role, idx) => (
                              <span key={idx} className="badge bg-light text-dark border">
                                {getRoleLabel(role)}
                              </span>
                            ))}
                            </div>
                        </div>
                        </div>
                    )}

                  {/* Document Tab */}
                  {activeTab === 'document' && (
                    <div>
                      <div className="mb-3 d-flex gap-2">
                        <input type="text" className="form-control" placeholder="Search ..." />
                        <button className="btn btn-outline-secondary">Export</button>
                        <button className="btn btn-primary">Upload Document</button>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-vcenter table-hover">
                          <thead>
                            <tr>
                              <th>CATEGORY</th>
                              <th>DOCUMENT NAME</th>
                              <th>FILE NAME</th>
                              <th>SIZE</th>
                              <th>UPLOAD DATE</th>
                              <th>UPLOAD BY</th>
                              <th>STATUS</th>
                              <th>ACTION</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentsData.map(doc => (
                              <tr key={doc.id}>
                                <td><span className="badge bg-light text-dark">{doc.category}</span></td>
                                <td><strong>{doc.name}</strong></td>
                                <td><a href="#" className="text-primary">{doc.fileName}</a></td>
                                <td>{doc.size}</td>
                                <td>{doc.uploadDate}</td>
                                <td>{doc.uploadBy}</td>
                                <td>{getStatusBadge(doc.status)}</td>
                                <td>
                                  <div className="btn-list flex-nowrap">
                                    <a href="#" className="btn btn-icon btn-ghost-primary">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>
                                    </a>
                                    <a href="#" className="btn btn-icon btn-ghost-danger">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                      </svg>
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Activity Log Tab */}
                  {activeTab === 'activity' && (
                    <div>
                      <div className="mb-3 d-flex gap-2 align-items-center justify-content-between">
                        <div className="d-flex gap-2 align-items-center flex-grow-1">
                          <div className="position-relative flex-grow-1" style={{maxWidth: '400px'}}>
                            <svg className="position-absolute top-50 start-0 translate-middle-y ms-2" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                            </svg>
                            <input
                              className="form-control ps-4"
                              placeholder="Search ..."
                              value={activityLogSearch}
                              onChange={(e) => setActivityLogSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        <button className="btn btn-outline-primary d-flex align-items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1H1a.5.5 0 0 1-.5-.5zM0 4.5a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5zM2.5 7a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
                          </svg>
                          Export
                        </button>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Date/Time</th>
                              <th>User</th>
                              <th>Action</th>
                              <th>Status Change</th>
                              <th>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activityLogLoading ? (
                              <tr>
                                <td colSpan="5" className="text-center py-4">
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : activityLogRows.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center py-4 text-muted">
                                  No activity log found for this user
                                </td>
                              </tr>
                            ) : (
                              activityLogRows
                                .filter(row => 
                                  !activityLogSearch || 
                                  row.entityType?.toLowerCase().includes(activityLogSearch.toLowerCase()) ||
                                  row.actionType?.toLowerCase().includes(activityLogSearch.toLowerCase()) ||
                                  row.description?.toLowerCase().includes(activityLogSearch.toLowerCase())
                                )
                                .map(row => (
                                  <tr key={row.id}>
                                    <td className="small text-muted">{formatDateTime(row.actionTimestamp)}</td>
                                    <td className="small">{row.createdBy || 'Unknown'}</td>
                                    <td>{getActionBadge(row.actionType)}</td>
                                    <td className="small">
                                      {row.statusChange && row.statusChange.oldStatus && row.statusChange.newStatus ? (
                                        <>
                                          {getAuditStatusBadge(row.statusChange.oldStatus)} → {getAuditStatusBadge(row.statusChange.newStatus)}
                                        </>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => handleViewActivityDetails(row)}
                                      >
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mt-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Show</span>
                          <select 
                            className="form-select form-select-sm" 
                            style={{width: '80px'}}
                            value={activityLogSize}
                            onChange={(e) => {
                              const newSize = Number(e.target.value);
                              setActivityLogSize(newSize);
                              fetchActivityLog(selectedUser?.id, 0, newSize);
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                          <span className="text-muted">of {activityLogTotalElements} entries</span>
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm m-0">
                            <li className={`page-item ${activityLogPage === 0 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => fetchActivityLog(selectedUser?.id, activityLogPage - 1, activityLogSize)}
                                disabled={activityLogPage === 0}
                              >
                                ←
                              </button>
                            </li>
                            {Array.from({ length: Math.min(activityLogTotalPages, 5) }, (_, i) => {
                              const pageNum = Math.max(0, activityLogPage - 2) + i;
                              if (pageNum >= activityLogTotalPages) return null;
                              return (
                                <li key={pageNum} className={`page-item ${pageNum === activityLogPage ? 'active' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => fetchActivityLog(selectedUser?.id, pageNum, activityLogSize)}
                                  >
                                    {pageNum + 1}
                                  </button>
                                </li>
                              );
                            })}
                            <li className={`page-item ${activityLogPage >= activityLogTotalPages - 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => fetchActivityLog(selectedUser?.id, activityLogPage + 1, activityLogSize)}
                                disabled={activityLogPage >= activityLogTotalPages - 1}
                              >
                                →
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>

                      {/* Activity Details Modal */}
                      {showActivityDetails && selectedActivityEntry && (
                        <>
                          <div className="modal fade show" style={{display: 'block'}} tabIndex="-1" role="dialog" aria-hidden="false">
                            <div className="modal-dialog modal-lg" role="document">
                              <div className="modal-content">
                                <div className="modal-header border-bottom">
                                  <h5 className="modal-title">Activity History Details</h5>
                                  <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowActivityDetails(false)}
                                    aria-label="Close"
                                  ></button>
                                </div>
                                <div className="modal-body">
                                  <div className="row">
                                    <div className="col-md-6">
                                      <h6>Status Information</h6>
                                      <div className="mb-2">
                                        <strong>Previous Status:</strong><br/>
                                        {selectedActivityEntry.statusChange && selectedActivityEntry.statusChange.oldStatus ? getAuditStatusBadge(selectedActivityEntry.statusChange.oldStatus) : 'N/A'}
                                      </div>
                                      <div className="mb-2">
                                        <strong>New Status:</strong><br/>
                                        {selectedActivityEntry.statusChange && selectedActivityEntry.statusChange.newStatus ? getAuditStatusBadge(selectedActivityEntry.statusChange.newStatus) : 'N/A'}
                                      </div>
                                      <div className="mb-2">
                                        <strong>Entity Type:</strong><br/>
                                        {formatEntityType(selectedActivityEntry.entityType)}
                                      </div>
                                      <div className="mb-2">
                                        <strong>Entity ID:</strong><br/>
                                        <code>{selectedActivityEntry.entityId || 'N/A'}</code>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <h6>User Information</h6>
                                      <div className="mb-2">
                                        <strong>User:</strong><br/>
                                        <span className="fw-medium">{selectedActivityEntry.createdBy || 'Unknown User'}</span>
                                      </div>
                                      <div className="mb-2">
                                        <strong>Date:</strong><br/>
                                        <span className="text-muted">{formatDateTime(selectedActivityEntry.actionTimestamp)}</span>
                                      </div>
                                      {selectedActivityEntry.ipAddress && (
                                        <div className="mb-2">
                                          <strong>IP Address:</strong><br/>
                                          <code>{selectedActivityEntry.ipAddress}</code>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <hr />
                                  {selectedActivityEntry.description && (
                                    <div>
                                      <strong>Description:</strong>
                                      <p className="text-muted">{selectedActivityEntry.description}</p>
                                    </div>
                                  )}
                                  {selectedActivityEntry.reviewNotes && (
                                    <div>
                                      <strong>Review Notes:</strong>
                                      <p className="text-muted">{selectedActivityEntry.reviewNotes}</p>
                                    </div>
                                  )}
                                  {selectedActivityEntry.additionalData && (
                                    <div>
                                      <strong>Additional Data:</strong>
                                      <pre className="bg-light p-2 rounded small">
                                        {JSON.stringify(selectedActivityEntry.additionalData, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                                <div className="modal-footer border-top">
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowActivityDetails(false)}
                                  >
                                    Close
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="modal-backdrop fade show" onClick={() => setShowActivityDetails(false)}></div>
                        </>
                      )}
                    </div>
                  )}

                    {/* Competency Tab */}
                    {activeTab === 'competency' && (
                      <div>
                        <div className="mb-3 d-flex gap-2 align-items-end">
                          <input
                            className="form-control"
                            placeholder="Search by name, standard..."
                            value={competencySearch}
                            onChange={(e) => setCompetencySearch(e.target.value)}
                          />
                          <select
                            className="form-select"
                            style={{width: '200px'}}
                            value={competencyStandardFilter}
                            onChange={(e) => setCompetencyStandardFilter(e.target.value)}
                          >
                            <option value="">All Standards</option>
                            {competencyStandardOptions.map((standard) => (
                              <option key={standard} value={standard}>{standard}</option>
                            ))}
                          </select>
                          <select
                            className="form-select"
                            style={{width: '200px'}}
                            value={competencyStatusFilter}
                            onChange={(e) => setCompetencyStatusFilter(e.target.value)}
                          >
                            <option value="">All Status</option>
                            <option value="pending">Pending Approval</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="need_revision">Need Revision</option>
                          </select>
                          <button className="btn btn-outline-secondary" type="button">Filter</button>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-vcenter card-table table-hover">
                            <thead>
                              <tr>
                                {/* <th style={{width: '40px'}}><input type="checkbox" className="form-check-input" /></th> */}
                                <th>Type</th>
                                <th>Standard</th>
                                <th>Qualification</th>
                                <th>Sector/Scheme</th>
                                <th>Request Date</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {competencyLoading ? (
                                <tr>
                                  <td colSpan="9" className="text-center py-4">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : filteredCompetencyRows.length === 0 ? (
                                <tr>
                                  <td colSpan="9" className="text-center py-4 text-muted">
                                    No competency requests found for this user
                                  </td>
                                </tr>
                              ) : (
                                filteredCompetencyRows.map(row => {
                                  const isUpgrade = row.requestType === 'Upgrade';
                                  return (
                                    <tr key={row.id}>
                                      {/* <td><input type="checkbox" className="form-check-input" /></td> */}
                                      <td>
                                        {isUpgrade ? (
                                          <span className="badge bg-purple text-white">Upgrade</span>
                                        ) : (
                                          <span className="badge bg-cyan text-white">New</span>
                                        )}
                                      </td>
                                      <td>{row.standard}</td>
                                      <td>
                                        {isUpgrade && row.currentTypeName && row.requestedTypeName ? (
                                          <div className="d-flex align-items-center gap-1">
                                            <span className="badge bg-info text-light">{row.currentTypeName}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            <span className="badge bg-success text-white">{row.requestedTypeName}</span>
                                          </div>
                                        ) : (
                                          <span className="badge bg-info text-white">{row.qualificationName || 'N/A'}</span>
                                        )}
                                      </td>
                                      <td><span className="text-muted small">{row.sectors}</span></td>
                                      <td>{formatDate(row.requestDate)}</td>
                                      <td>{competencyStatusBadge(row.status)}</td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mt-3">
                          <div>
                            <label className="form-label small mb-1">Records per page</label>
                            <select className="form-select form-select-sm" style={{width: '100px'}}>
                              <option>10</option>
                              <option>25</option>
                              <option>50</option>
                            </select>
                          </div>
                          <nav>
                            <ul className="pagination pagination-sm m-0">
                              <li className="page-item"><a className="page-link" href="#">←</a></li>
                              <li className="page-item active"><a className="page-link" href="#">1</a></li>
                              <li className="page-item"><a className="page-link" href="#">2</a></li>
                              <li className="page-item"><a className="page-link" href="#">→</a></li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    )}

                    {/* Performance Tab */}
                    {activeTab === 'performance' && (
                      <div>
                        <div className="mb-3">
                          <select className="form-select">
                            <option>Select performance</option>
                            <option>Audit</option>
                            <option>Project</option>
                            <option>Task</option>
                          </select>
                        </div>
                        
                        <div className="row mb-3 g-2">
                          <div className="col-sm-6 col-lg-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="d-flex align-items-baseline">
                                  <div className="h2 mb-0">2</div>
                                  <div className="ms-auto text-muted">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#9ca3af" stroke="currentColor" strokeWidth="2">
                                      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                                      <polyline points="13 2 13 9 20 9"/>
                                    </svg>
                                  </div>
                                </div>
                                <div className="text-muted mt-2">Audit</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="d-flex align-items-baseline">
                                  <div className="h2 mb-0">2</div>
                                  <div className="ms-auto text-muted">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#9ca3af" stroke="currentColor" strokeWidth="2">
                                      <rect x="3" y="3" width="7" height="7"/>
                                      <rect x="14" y="3" width="7" height="7"/>
                                      <rect x="14" y="14" width="7" height="7"/>
                                      <rect x="3" y="14" width="7" height="7"/>
                                    </svg>
                                  </div>
                                </div>
                                <div className="text-muted mt-2">Project</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="d-flex align-items-baseline">
                                  <div className="h2 mb-0">2</div>
                                  <div className="ms-auto text-muted">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#9ca3af" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10"/>
                                      <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                  </div>
                                </div>
                                <div className="text-muted mt-2">Hours</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mb-3 mt-4">
                          <h4>Performance</h4>
                        </div>
                        
                        <div className="mb-3 d-flex gap-2">
                          <input type="text" className="form-control" placeholder="Search ..." />
                          <button className="btn btn-outline-secondary" title="Filter">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                            </svg>
                          </button>
                          <button className="btn btn-outline-secondary">Export</button>
                        </div>
                        
                        <div className="table-responsive">
                          <table className="table table-vcenter table-hover">
                            <thead>
                              <tr>
                                <th>PROJECT</th>
                                <th>CLIENT</th>
                                <th>SERVICES</th>
                                <th>Mandays</th>
                                <th>STATUS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {performanceData.map(perf => (
                                <tr key={perf.id}>
                                  <td><strong>{perf.project}</strong></td>
                                  <td>{perf.client}</td>
                                  <td>{perf.services}</td>
                                  <td>{perf.mandays}</td>
                                  <td>
                                    {perf.status === 'success' ? (
                                      <span className="badge bg-success">Success</span>
                                    ) : (
                                      <span className="badge bg-secondary">{perf.status}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <select className="form-select" style={{width: 'auto'}}>
                              <option>10</option>
                              <option>25</option>
                              <option>50</option>
                            </select>
                          </div>
                          <nav>
                            <ul className="pagination pagination-sm m-0">
                              <li className="page-item"><a className="page-link" href="#">←</a></li>
                              <li className="page-item active"><a className="page-link" href="#">1</a></li>
                              <li className="page-item"><a className="page-link" href="#">2</a></li>
                              <li className="page-item"><a className="page-link" href="#">→</a></li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && selectedUser && (
                      <div>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="card">
                              <div className="card-body">
                                <h6 className="mb-2">Reset Password</h6>
                                <p className="text-muted small">Send a password reset link to the user's email address.</p>
                                <div className="d-flex gap-2">
                                  <button className="btn btn-danger" onClick={handleResetPassword}>Reset Password</button>
                                  <button className="btn btn-outline-secondary" onClick={() => setPasswordResetMessage('')}>Clear</button>
                                </div>
                                {passwordResetMessage && (
                                  <div className="mt-3"><span className="text-success">{passwordResetMessage}</span></div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="card">
                              <div className="card-body">
                                <h6 className="mb-2">Account Data</h6>
                                <div className="mb-2">
                                  <label className="form-label small">Username</label>
                                  <input name="username" value={accountForm.username} onChange={handleAccountChange} className="form-control" />
                                </div>
                                <div className="mb-2">
                                  <label className="form-label small">Email</label>
                                  <input name="email" value={accountForm.email} onChange={handleAccountChange} className="form-control" />
                                </div>
                                <div className="mb-2">
                                  <label className="form-label small">Employee ID</label>
                                  <input name="empId" value={accountForm.empId} onChange={handleAccountChange} className="form-control" />
                                </div>
                                <div className="mb-2">
                                  <label className="form-label small">Emergency Contact</label>
                                  <input name="emergencyContact" value={accountForm.emergencyContact} onChange={handleAccountChange} className="form-control" />
                                </div>
                                <div className="mb-2">
                                  <label className="form-label small">Emergency Address</label>
                                  <input name="emergencyAddress" value={accountForm.emergencyAddress} onChange={handleAccountChange} className="form-control" />
                                </div>
                                <div className="d-flex justify-content-end">
                                  <button className="btn btn-primary" onClick={handleSaveAccount}>Save Changes</button>
                                </div>
                                {passwordResetMessage && (
                                  <div className="mt-3"><span className="text-success">{passwordResetMessage}</span></div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="card">
                              <div className="card-body d-flex align-items-center justify-content-between">
                                <div>
                                  <h6 className="mb-1">Delete User</h6>
                                  <div className="text-muted small">This action cannot be undone.</div>
                                </div>
                                <button className="btn btn-outline-danger" onClick={handleDeleteUser}>Delete User</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Create User Modal */}
    <div className="modal modal-blur fade" id="modal-create-user" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <form onSubmit={handleCreateUser} noValidate>
            <div className="modal-header">
              <h5 className="modal-title">Create User</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">First Name <span className="text-danger">*</span></label>
                  <input className="form-control" name="firstName" value={createForm.firstName} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Last Name <span className="text-danger">*</span></label>
                  <input className="form-control" name="lastName" value={createForm.lastName} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Username <span className="text-danger">*</span></label>
                  <input className="form-control" name="username" value={createForm.username} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email <span className="text-danger">*</span></label>
                  <input className="form-control" name="email" type="email" value={createForm.email} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Password <span className="text-danger">*</span></label>
                  <input className="form-control" name="password" type="password" value={createForm.password} onChange={handleCreateChange} autoComplete="new-password" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Employee ID <span className="text-danger">*</span></label>
                  <input className="form-control" name="employeeId" value={createForm.employeeId} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="departmentId"
                      value={createForm.departmentId}
                      onChange={handleCreateSelectChange}
                      options={departments}
                      placeholder="Select department"
                      label="Department"
                      required={true}
                      error={null}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="jobTitleId"
                      value={createForm.jobTitleId}
                      onChange={handleCreateSelectChange}
                      options={jobTitles}
                      placeholder="Select job title"
                      label="Job Title"
                      required={true}
                      error={null}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                  <input className="form-control" name="nohp" value={createForm.nohp} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Emergency Contact</label>
                  <input className="form-control" name="emergencyContact" value={createForm.emergencyContact} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="branchIds"
                      value={createForm.branchIds}
                      onChange={handleCreateSelectChange}
                      options={branches}
                      placeholder="Select branches"
                      label="Branch"
                      required={false}
                      error={null}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                      multiple={true}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="roles"
                      value={createForm.roles}
                      onChange={handleCreateSelectChange}
                      options={roleOptions}
                      placeholder="Select roles"
                      label="Roles"
                      required={true}
                      error={null}
                      valueField="value"
                      labelField="label"
                      searchField="label"
                      multiple={true}
                      disabled={isCreating || isLoadingRoles}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">Address <span className="text-danger">*</span></label>
                  <textarea className="form-control" name="address" value={createForm.address} onChange={handleCreateChange} rows="3" />
                </div>
                <div className="col-12">
                  <label className="form-label">Emergency Address</label>
                  <textarea className="form-control" name="emergencyAddress" value={createForm.emergencyAddress} onChange={handleCreateChange} rows="3" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Country <span className="text-danger">*</span></label>
                  <input className="form-control" name="country" value={createForm.country} onChange={handleCreateChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">State <span className="text-danger">*</span></label>
                  <input className="form-control" name="state" value={createForm.state} onChange={handleCreateChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">City <span className="text-danger">*</span></label>
                  <input className="form-control" name="city" value={createForm.city} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Postal Code <span className="text-danger">*</span></label>
                  <input className="form-control" name="postalCode" value={createForm.postalCode} onChange={handleCreateChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status <span className="text-danger">*</span></label>
                  <select className="form-select" name="status" value={createForm.status} onChange={handleCreateChange}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="col-12">
                  <hr className="my-2" />
                  <h6 className="mb-2">Profile &amp; Signature</h6>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Profile Picture</label>
                  <input className="form-control" type="file" accept=".jpg,.jpeg,.png,.gif" onChange={handleCreateProfilePictureChange} />
                  <div className="form-text">Max size: 5MB. Supported formats: JPG, PNG, GIF</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Signature</label>
                  <input className="form-control" type="file" accept=".jpg,.jpeg,.png,.gif" onChange={handleCreateSignatureChange} />
                  <div className="form-text">Max size: 2MB. Supported formats: JPG, PNG, GIF</div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" className="btn btn-success" disabled={isCreating}>
                {isCreating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {/* Edit User Modal */}
    <div className="modal modal-blur fade" id="modal-edit-user" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <form onSubmit={handleUpdateUser} noValidate>
            <div className="modal-header">
              <h5 className="modal-title">Edit User</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">First Name <span className="text-danger">*</span></label>
                  <input className="form-control" name="firstName" value={editForm.firstName} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Last Name <span className="text-danger">*</span></label>
                  <input className="form-control" name="lastName" value={editForm.lastName} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Username <span className="text-danger">*</span></label>
                  <input className="form-control" name="username" value={editForm.username} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email <span className="text-danger">*</span></label>
                  <input className="form-control" name="email" type="email" value={editForm.email} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Employee ID <span className="text-danger">*</span></label>
                  <input className="form-control" name="employeeId" value={editForm.employeeId} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="departmentId"
                      value={editForm.departmentId}
                      onChange={handleEditSelectChange}
                      options={departments}
                      placeholder="Select department"
                      label="Department"
                      required={true}
                      error={null}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="jobTitleId"
                      value={editForm.jobTitleId}
                      onChange={handleEditSelectChange}
                      options={jobTitles}
                      placeholder="Select job title"
                      label="Job Title"
                      required={true}
                      error={null}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="branchIds"
                      value={editForm.branchIds}
                      onChange={handleEditSelectChange}
                      options={branches}
                      placeholder="Select branches"
                      label="Branch"
                      required={false}
                      error={null}
                      valueField="id"
                      labelField="name"
                      searchField="name"
                      multiple={true}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <TomSelect
                      name="roles"
                      value={editForm.roles}
                      onChange={handleEditSelectChange}
                      options={roleOptions}
                      placeholder="Select roles"
                      label="Roles"
                      required={true}
                      error={null}
                      valueField="value"
                      labelField="label"
                      searchField="label"
                      multiple={true}
                      disabled={isUpdating || isLoadingRoles}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                  <input className="form-control" name="nohp" value={editForm.nohp} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Emergency Contact</label>
                  <input className="form-control" name="emergencyContact" value={editForm.emergencyContact} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status <span className="text-danger">*</span></label>
                  <select className="form-select" name="status" value={editForm.status} onChange={handleEditChange}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Address <span className="text-danger">*</span></label>
                  <textarea className="form-control" name="address" value={editForm.address} onChange={handleEditChange} rows="3" />
                </div>
                <div className="col-12">
                  <label className="form-label">Emergency Address</label>
                  <textarea className="form-control" name="emergencyAddress" value={editForm.emergencyAddress} onChange={handleEditChange} rows="3" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Country <span className="text-danger">*</span></label>
                  <input className="form-control" name="country" value={editForm.country} onChange={handleEditChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">State <span className="text-danger">*</span></label>
                  <input className="form-control" name="state" value={editForm.state} onChange={handleEditChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">City <span className="text-danger">*</span></label>
                  <input className="form-control" name="city" value={editForm.city} onChange={handleEditChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Postal Code <span className="text-danger">*</span></label>
                  <input className="form-control" name="postalCode" value={editForm.postalCode} onChange={handleEditChange} />
                </div>

                <div className="col-12">
                  <hr className="my-2" />
                  <h6 className="mb-2">Profile &amp; Signature</h6>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Profile Picture</label>
                  <input className="form-control" type="file" accept=".jpg,.jpeg,.png,.gif" onChange={handleEditProfilePictureChange} />
                  <div className="form-text">Max size: 5MB. Supported formats: JPG, PNG, GIF</div>
                  {editProfilePreview && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        <img
                          src={editProfilePreview}
                          alt="Profile preview"
                          className="border rounded"
                          style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', cursor: 'default' }}
                        />
                        {editProfilePicture && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={clearEditProfilePictureSelection}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Signature</label>
                  <input className="form-control" type="file" accept=".jpg,.jpeg,.png,.gif" onChange={handleEditSignatureChange} />
                  <div className="form-text">Max size: 2MB. Supported formats: JPG, PNG, GIF</div>
                  {editSignaturePreview && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        <img
                          src={editSignaturePreview}
                          alt="Signature preview"
                          className="border rounded"
                          style={{ maxWidth: '150px', maxHeight: '80px', objectFit: 'cover', cursor: 'default' }}
                        />
                        {editSignature && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={clearEditSignatureSelection}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={isUpdating}>
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <SuccessModal
      isOpen={showSuccessModal}
      onClose={() => setShowSuccessModal(false)}
      title="Success"
      message={successMessage}
      buttonText="OK"
    />
    <FailModal
      isOpen={showFailModal}
      onClose={() => setShowFailModal(false)}
      title="Operation Failed"
      message={failMessage}
      buttonText="Close"
    />
    </>
  );
};

export default ListUsers;
